import { useState, useEffect } from 'react';

import {
  getCustomerServiceEmail,
  getCustomerServicePhone,
  getCustomerServicePhoneLink
} from '../utils/customerService';
import {
  CUSTOMER_SERVICE_CONTACTS_UPDATED,
  fetchCustomerServiceContacts
} from '../services/brandingService';

const readContacts = () => ({
  phone: getCustomerServicePhone(),
  phoneLink: getCustomerServicePhoneLink(),
  email: getCustomerServiceEmail()
});

/**
 * The brand's customer service contacts, for screens that offer them to the user.
 * Renders immediately with whatever is already known — the cache from an earlier visit,
 * or the hardcoded fallback — and re-renders once the branding endpoint answers. Asking
 * for the contacts is what triggers the fetch, so a visitor who lands straight on
 * /forgotPassword gets them without having seen the login page.
 * @returns {{phone: string, phoneLink: string, email: string}}
 */
const useCustomerServiceContacts = () => {
  const [contacts, setContacts] = useState(readContacts);

  useEffect(() => {
    // Replaced only on a real change, so that the catch-up read below and any repeat
    // event do not re-render every screen showing these details for nothing.
    const update = () => setContacts((previous) => {
      const next = readContacts();
      const unchanged = next.phone === previous.phone
        && next.phoneLink === previous.phoneLink
        && next.email === previous.email;

      return unchanged ? previous : next;
    });

    document.addEventListener(CUSTOMER_SERVICE_CONTACTS_UPDATED, update);
    // A request that was already in flight when this hook rendered can have stored its
    // contacts before the listener above existed, and its event would then have gone
    // unheard. Read the cache once more now that we are subscribed.
    update();
    fetchCustomerServiceContacts();

    return () => {
      document.removeEventListener(CUSTOMER_SERVICE_CONTACTS_UPDATED, update);
    };
  }, []);

  return contacts;
};

export default useCustomerServiceContacts;
